<template>
  <div ref="autocomplete" class="app-autocomplete" :class="{ dense, borderless }">
    <AppInput
      ref="autocompleteInputEl"
      v-model="searchText"
      :label="label"
      :placeholder="placeholder"
      :error="invalid"
      :error-messages="errorMessages"
      :hide-details="hideDetails"
      @keydown="onKeydown"
      @focus="onFocus"
      @blur="onBlur"
    >
      <template v-if="loading || clearable" #append>
        <Loading v-if="loading" color="primary" />
        <!-- TODO: Vue 2.7 does not automatically attach native DOM listeners passed to a
        custom component root element. Keep the <span> wrapper for Vue 2 support.
        When Vue 2 is dropped, move @mousedown.prevent="reset" to <Close />. -->
        <span v-if="clearable && hasValue" class="close-button" @mousedown.prevent="reset">
          <Close />
        </span>
      </template>
    </AppInput>

    <ul v-if="showResult && filteredItems?.length" ref="autocompleteResultEl">
      <li
        v-for="(item, index) in filteredItems.slice(0, props.limit ?? undefined)"
        :key="index"
        :class="{ selected: isSelected(item) }"
        role="item"
        @mousedown.left="onSelect(item)"
        @mouseover="focusResultItem(index)"
        @mouseleave="focusResultItem(undefined)"
      >
        {{ typeof item === 'string' ? item : item.text }}
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'

import { isObject, isSameShallowObject } from '@staff/shared'

import Loading from '../../icons/Loading.vue'
import Close from '../../icons/Close.vue'
import { useInputValidation, useDetectOutsideClick } from '../../composables'
import { useElementRepositioning } from '../../composables/useElementRepositioning'
import AppInput from '../AppInput/AppInput.vue'

type Item = { text: string; value: unknown } | string

const props = defineProps<{
  id?: string | number
  label?: string
  labelAsSpan?: boolean
  hint?: string | string[]
  hideDetails?: boolean
  success?: boolean
  successMessages?: string | string[]
  warning?: boolean
  warningMessages?: string | string[]
  error?: boolean
  errorMessages?: string | string[]
  disabled?: boolean
  rules?: ({ id: string; rule: (value: unknown) => boolean | string | Promise<boolean | string> } | null)[]
  validationOptions?: {
    validateOnBlur?: boolean
    validateOnInput?: boolean
    validateOnModelChange?: boolean
  }
  modelValue?: unknown
  value?: unknown
  items?: Item[]
  placeholder?: string
  limit?: number
  loading?: boolean
  clearable?: boolean
  noFilter?: boolean
  dense?: boolean
  borderless?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: unknown): void
  (e: 'input', value: unknown): void
}>()

const autocomplete = ref<HTMLDivElement>()
const autocompleteInputEl = ref<typeof AppInput>()
const autocompleteResultEl = ref<HTMLUListElement>()

// Vue 2 v-model sends 'value', Vue 3 sends 'modelValue'
const currentValue = computed(() => props.modelValue ?? props.value)

const hasValue = computed(() => {
  const val = currentValue.value
  if (val === undefined || val === null || val === '') return false
  if (typeof val === 'object' && Object.keys(val as object).length === 0) return false
  return true
})

// Internal value tracks currentValue but is updated synchronously on select,
// so validation reads the correct value even before the prop round-trips.
const internalValue = ref<unknown>(currentValue.value)
watch(currentValue, (v) => {
  internalValue.value = v
})

const { invalid, errorMessages, validate } = useInputValidation(
  () => internalValue.value,
  () => props.rules,
  autocomplete,
)

const { addRepositionListener, removeRepositionListener } = useElementRepositioning(
  autocompleteResultEl,
  computed(() => ({
    root: autocompleteInputEl.value?.$el?.querySelector('.input-wrapper'),
    offsetY: 0,
  })),
)

const searchText = ref('')

const filteredItems = computed(() => {
  if (props.noFilter || !searchText.value) {
    return props.items
  }

  return props.items?.filter((item) => {
    const text = typeof item === 'string' ? item : item.text
    return text.toLowerCase().includes(searchText.value.toLowerCase())
  })
})

const onFocus = () => {
  searchText.value = ''
  showResult.value = true
}

const onBlur = () => {
  if (props.validationOptions?.validateOnBlur !== false) {
    validate()
  }

  close()
}

const onKeydown = (event: KeyboardEvent) => {
  if (event.key === 'ArrowUp') {
    event.preventDefault()
    onArrowUp()
    return
  }

  if (event.key === 'ArrowDown') {
    event.preventDefault()
    onArrowDown()
    return
  }

  if (event.key === 'Enter') {
    event.preventDefault()
    onSelectFocusedResultItem()
  }
}

const onSelect = (item: Item) => {
  const value = typeof item === 'string' ? item : item.value
  searchText.value = typeof item === 'string' ? item : item.text
  internalValue.value = value
  emit('update:modelValue', value)
  emit('input', value)
  showResult.value = false
}

const onSelectFocusedResultItem = () => {
  const item = filteredItems.value?.[resultFocusIndex.value ?? 0]

  if (item) {
    onSelect(item)
    autocompleteInputEl.value?.$el.querySelector('input')?.blur()
  }
}

const reset = () => {
  internalValue.value = undefined
  searchText.value = ''
  emit('update:modelValue', undefined)
  emit('input', undefined)
}

const isSelected = (item: Item) => {
  if (typeof item === 'string') {
    return currentValue.value === item
  }

  if (isObject(item.value) && isObject(currentValue.value)) {
    return isSameShallowObject(item.value, currentValue.value)
  }

  return item.value === currentValue.value
}

const syncSearchWithModelValue = () => {
  const selectedItem = props.items?.find((item) => isSelected(item))
  searchText.value = selectedItem ? (typeof selectedItem === 'string' ? selectedItem : selectedItem.text) : ''
}

watch([currentValue, () => props.items], syncSearchWithModelValue, { immediate: true })

watch(currentValue, () => {
  if (props.validationOptions?.validateOnInput) {
    validate()
  }
})

const close = () => {
  if (showResult.value) {
    showResult.value = false
  }
  syncSearchWithModelValue()
}

useDetectOutsideClick(autocomplete, close)

const showResult = ref(false)

watch(showResult, (show) => {
  resultFocusIndex.value = undefined

  if (show) {
    addRepositionListener()
  } else {
    removeRepositionListener()
  }
})

const resultFocusIndex = ref<number>()
const focusResultItem = (index: number | undefined) => {
  resultFocusIndex.value = index

  Array.from(autocompleteResultEl.value?.children ?? []).forEach((child, i) => {
    if (i === resultFocusIndex.value) {
      child.setAttribute('aria-selected', 'true')
      child.scrollIntoView({ block: 'nearest' })
    } else {
      child.removeAttribute('aria-selected')
    }
  })
}

const onArrowUp = () => {
  if (!autocompleteResultEl.value) return

  const resultCount = autocompleteResultEl.value.children.length

  const nextIndex = (resultFocusIndex.value ?? resultCount) - 1

  resultFocusIndex.value = nextIndex >= 0 ? nextIndex : resultCount - 1

  focusResultItem(resultFocusIndex.value)
}

const onArrowDown = () => {
  if (!autocompleteResultEl.value) return

  const resultCount = autocompleteResultEl.value.children.length

  const nextIndex = (resultFocusIndex.value ?? -1) + 1

  resultFocusIndex.value = nextIndex < resultCount ? nextIndex : 0

  focusResultItem(resultFocusIndex.value)
}
</script>

<style lang="scss" scoped>
.app-autocomplete {
  position: relative;

  .close-button {
    cursor: pointer;
    display: flex;
    align-items: center;
  }

  ul {
    position: absolute;
    width: 100%;
    margin: 0;
    padding: 0;
    z-index: 2;

    max-height: 250px;
    overflow-y: auto;

    box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;

    background-color: var(--color-canvas-default);
    border: 1.5px solid var(--color-border-default);
    border-radius: 16px;

    list-style: none;
    cursor: pointer;
    font-family: inherit;

    li {
      padding: 4px 16px;
      min-height: 48px;
      display: flex;
      align-items: center;

      border-top: 1px solid var(--color-border-divider);
      border-bottom: 1px solid var(--color-border-divider);

      &.selected {
        background-color: var(--color-brand-subtle);
      }

      & + li,
      &:first-of-type {
        border-top: 0;
      }

      &:last-of-type {
        border-bottom: 0;
      }

      &[aria-selected='true'] {
        background-color: var(--color-canvas-hover);
      }
    }
  }

  &.dense {
    .app-input {
      margin-bottom: 0;
    }

    ul {
      border-radius: 6px;
      border-width: 1px;
      margin-top: 4px;

      li {
        min-height: 36px;
        padding: 4px 12px;
        font-size: 14px;
      }
    }
  }

  &.borderless {
    margin-bottom: 0;

    :deep(.input-wrapper) {
      border: none;
    }
  }
}
</style>
